import { useEffect, useMemo, useState } from "react";
import { PageHeader } from "@/components/AppLayout";
import { api } from "@/lib/api";
import { Plus, X, Trash2, Pencil, Upload, RefreshCw, Users } from "lucide-react";
import ImportClassesModal from "@/components/ImportClassesModal";
import DateNav, { todayISO, formatDateLabel } from "@/components/DateNav";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

// Preset class types with brand colors. Owners can add custom types via "Other…".
const TYPE_PRESETS = [
  { value: "gi", label: "Gi", color: "#3ddc7e" },
  { value: "no-gi", label: "No-Gi", color: "#4f8eff" },
  { value: "kids", label: "Kids", color: "#f4b942" },
  { value: "peewee", label: "Peewee", color: "#ff7eb6" },
  { value: "open", label: "Open Mat", color: "#b266ff" },
  { value: "muay-thai", label: "Muay Thai", color: "#ff7a3d" },
  { value: "mma", label: "MMA", color: "#e04141" },
  { value: "boxing", label: "Boxing", color: "#d43a5e" },
  { value: "cross", label: "Cross Training", color: "#39c5cf" },
  { value: "sparring", label: "Sparring", color: "#ffb84d" },
  { value: "grappling", label: "Grappling", color: "#8b7dff" },
];
const TYPE_COLOR = Object.fromEntries(TYPE_PRESETS.map(p => [p.value, p.color]));
const DEFAULT_CUSTOM_COLOR = "#a3a3a3";
const colorFor = (type) => TYPE_COLOR[type] || DEFAULT_CUSTOM_COLOR;

// Default time slots shown when the academy has no classes yet
const DEFAULT_TIMES = ["07:00", "12:00", "18:00"];

// Build a Monday → Sunday range label anchored to the given ISO date's week
function weekRangeForDate(iso) {
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  const day = dt.getDay(); // 0=Sun ... 6=Sat
  const monOffset = day === 0 ? -6 : 1 - day;
  const mon = new Date(dt); mon.setDate(dt.getDate() + monOffset);
  const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
  const fmt = (d) => d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  return `Week of ${fmt(mon)} — ${fmt(sun)}`;
}

// Weekday index (0=Mon ... 6=Sun) for a YYYY-MM-DD string
function weekdayFromISO(iso) {
  const [y, m, d] = iso.split("-").map(Number);
  const js = new Date(y, m - 1, d).getDay();
  return js === 0 ? 6 : js - 1;
}

function ClassCell({ klass, onClick }) {
  const color = colorFor(klass.type);
  return (
    <button onClick={onClick} className="card text-left p-2.5 w-full transition-all hover:scale-[1.01]"
      data-testid={`class-cell-${klass.class_id}`}
      style={{ borderLeft: `3px solid ${color}`, background: `${color}10` }}>
      <div className="font-semibold text-[12px] uppercase tracking-tight truncate">{klass.name}</div>
      <div className="label-tiny mt-1 truncate" style={{ fontSize: 10 }}>{klass.instructor}</div>
      <div className="mono text-[11px] mt-1.5" style={{ color }}>{klass.enrolled}/{klass.capacity}</div>
    </button>
  );
}

function ClassFormModal({ onClose, onSave, defaults, editing }) {
  const presetValues = TYPE_PRESETS.map(p => p.value);
  const initialType = editing?.type ?? defaults?.type ?? "gi";
  const initialIsCustom = !presetValues.includes(initialType);
  const [isCustom, setIsCustom] = useState(initialIsCustom);
  const [form, setForm] = useState({
    name: editing?.name ?? "Fundamentals",
    type: initialType,
    day_of_week: editing?.day_of_week ?? defaults?.day ?? 0,
    start_time: editing?.start_time ?? defaults?.time ?? "07:00",
    duration_min: editing?.duration_min ?? 60,
    capacity: editing?.capacity ?? 20,
    instructor: editing?.instructor ?? "",
    mat: editing?.mat ?? "Mat A",
  });

  const submit = async (e) => {
    e.preventDefault();
    const payload = { ...form, type: (form.type || "gi").toLowerCase().trim() };
    try {
      if (editing) {
        // No PUT endpoint yet — delete + recreate to update
        await api.delete(`/classes/${editing.class_id}`);
      }
      const { data } = await api.post("/classes", payload);
      onSave(data, editing?.class_id);
    } catch (err) {
      alert(err.response?.data?.detail || "Could not save class");
    }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", display: "grid", placeItems: "center", zIndex: 50, padding: 16 }}>
      <form onSubmit={submit} className="card w-full" style={{ maxWidth: 460, maxHeight: "90vh", overflowY: "auto" }} data-testid="class-form-modal">
        <div className="p-6">
          <div className="flex items-center justify-between mb-5">
            <div>
              <div className="label" style={{ color: "var(--green)" }}>{editing ? "Edit class" : "New class"}</div>
              <div className="text-[20px] font-bold uppercase mt-1">{editing ? "Update schedule" : "Add to schedule"}</div>
            </div>
            <button type="button" onClick={onClose} data-testid="modal-close"><X size={18} /></button>
          </div>

          <div className="space-y-4">
            <label className="block label">Name
              <input className="input mt-1" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="cf-name" required />
            </label>

            <div>
              <div className="label mb-2">Type</div>
              <div className="flex flex-wrap gap-2" data-testid="cf-type-chips">
                {TYPE_PRESETS.map(p => (
                  <button key={p.value} type="button"
                    onClick={() => { setIsCustom(false); setForm({ ...form, type: p.value }); }}
                    style={{
                      padding: "8px 12px", borderRadius: 8, fontSize: 12, fontWeight: 600,
                      border: `1px solid ${(!isCustom && form.type === p.value) ? p.color : "var(--border)"}`,
                      background: (!isCustom && form.type === p.value) ? `${p.color}20` : "var(--bg-card)",
                      color: (!isCustom && form.type === p.value) ? p.color : "var(--text)",
                      cursor: "pointer",
                    }}
                    data-testid={`cf-type-${p.value}`}>
                    <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: 2, background: p.color, marginRight: 6 }} />
                    {p.label}
                  </button>
                ))}
                <button type="button"
                  onClick={() => { setIsCustom(true); setForm({ ...form, type: isCustom ? form.type : "" }); }}
                  style={{
                    padding: "8px 12px", borderRadius: 8, fontSize: 12, fontWeight: 600,
                    border: `1px solid ${isCustom ? DEFAULT_CUSTOM_COLOR : "var(--border)"}`,
                    background: isCustom ? `${DEFAULT_CUSTOM_COLOR}20` : "var(--bg-card)",
                    color: isCustom ? DEFAULT_CUSTOM_COLOR : "var(--text)",
                    cursor: "pointer",
                  }}
                  data-testid="cf-type-other">
                  Other…
                </button>
              </div>
              {isCustom && (
                <input className="input mt-2" placeholder="e.g. self-defense, competition, women's class"
                  value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}
                  data-testid="cf-type-custom" required />
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <label className="block label">Day
                <select className="input mt-1" value={form.day_of_week} onChange={e => setForm({ ...form, day_of_week: Number(e.target.value) })} data-testid="cf-day">
                  {DAYS.map((d, i) => (<option key={d} value={i}>{d}</option>))}
                </select>
              </label>
              <label className="block label">Start time
                <input type="time" className="input mt-1" value={form.start_time}
                  onChange={e => setForm({ ...form, start_time: e.target.value })} data-testid="cf-time" required />
              </label>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <label className="block label">Duration (min)
                <input type="number" min="15" step="5" className="input mt-1" value={form.duration_min}
                  onChange={e => setForm({ ...form, duration_min: Number(e.target.value) })} data-testid="cf-duration" />
              </label>
              <label className="block label">Capacity
                <input type="number" min="1" className="input mt-1" value={form.capacity}
                  onChange={e => setForm({ ...form, capacity: Number(e.target.value) })} data-testid="cf-capacity" />
              </label>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <label className="block label">Instructor
                <input className="input mt-1" value={form.instructor}
                  onChange={e => setForm({ ...form, instructor: e.target.value })}
                  placeholder="e.g. Prof. Smith" data-testid="cf-instructor" required />
              </label>
              <label className="block label">Mat
                <input className="input mt-1" value={form.mat} onChange={e => setForm({ ...form, mat: e.target.value })} data-testid="cf-mat" />
              </label>
            </div>
          </div>

          <button type="submit" className="btn btn-primary w-full mt-5" data-testid="cf-submit">
            {editing ? "Save changes" : "Add class"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default function Classes() {
  const [classes, setClasses] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [selected, setSelected] = useState(null);
  const [showForm, setShowForm] = useState(null); // null | { day, time } | { editing: klass }
  const [showImport, setShowImport] = useState(false);
  const [busy, setBusy] = useState(false);
  const [date, setDate] = useState(todayISO());

  const load = () => {
    api.get("/classes", { params: { date } }).then(r => setClasses(r.data));
    api.get("/instructors").then(r => setInstructors(r.data));
  };
  useEffect(() => { load(); }, [date]);

  // Weekday index of the currently-selected date (0=Mon ... 6=Sun) — drives
  // column highlight in the grid so the picked day is visually anchored.
  const activeDow = useMemo(() => weekdayFromISO(date), [date]);

  // Build the list of time rows: union of all real class times + defaults if academy is empty
  const times = useMemo(() => {
    const set = new Set(classes.map(c => c.start_time));
    if (set.size === 0) DEFAULT_TIMES.forEach(t => set.add(t));
    return [...set].sort();
  }, [classes]);

  const grid = useMemo(() => {
    const g = {};
    for (const c of classes) g[`${c.day_of_week}-${c.start_time}`] = c;
    return g;
  }, [classes]);

  // Keep the detail-card data in sync with the latest classes fetch.
  // Without this, reservations added while a class is selected would appear stale.
  const freshSelected = useMemo(
    () => (selected ? classes.find(c => c.class_id === selected.class_id) || selected : null),
    [selected, classes]
  );

  const firstEmptySlot = () => {
    const slots = new Set(classes.map(c => `${c.day_of_week}-${c.start_time}`));
    for (let d = 0; d < DAYS.length; d++) for (const t of times) {
      if (!slots.has(`${d}-${t}`)) return { day: d, time: t };
    }
    return { day: 0, time: "07:00" };
  };

  const deleteClass = async (klass) => {
    if (!confirm(`Remove "${klass.name}" from ${DAYS[klass.day_of_week]} ${klass.start_time}?`)) return;
    setBusy(true);
    try {
      await api.delete(`/classes/${klass.class_id}`);
      setSelected(null);
      load();
    } finally { setBusy(false); }
  };

  const onSaved = (saved, replacedId) => {
    if (replacedId) {
      // Edit-mode replaced an existing class
      setClasses(prev => prev.filter(c => c.class_id !== replacedId).concat(saved));
    } else {
      setClasses(prev => [...prev, saved]);
    }
    setShowForm(null);
    setSelected(saved);
  };

  return (
    <div data-testid="page-classes">
      <PageHeader section="Operate" title="Class schedule"
        right={
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <DateNav date={date} onChange={setDate} testid="classes-datenav" />
            <button className="btn btn-outline" onClick={() => setShowImport(true)} data-testid="btn-import-classes-csv">
              <Upload size={14} />Import CSV
            </button>
            <button className="btn btn-primary" onClick={() => setShowForm({ defaults: firstEmptySlot() })} data-testid="btn-add-class">
              <Plus size={14} />Add class
            </button>
          </div>
        }
      />

      <div className="p-4 md:p-8 space-y-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="font-semibold text-[14px] md:text-[15px] uppercase tracking-tight" data-testid="week-label">{weekRangeForDate(date)}</div>
            <div className="label-tiny mt-1" data-testid="reservations-hint">
              Reservations shown for <span className="mono" style={{ color: "var(--green)" }}>{formatDateLabel(date)}</span>. Tap a class to view its roster.
            </div>
          </div>
          <div className="flex items-center gap-3 flex-wrap text-xs" data-testid="type-legend">
            {TYPE_PRESETS.map(p => (
              <span key={p.value} className="label-tiny flex items-center gap-1.5">
                <span style={{ width: 8, height: 8, borderRadius: 2, background: p.color }} />{p.label}
              </span>
            ))}
          </div>
        </div>

        {/* Horizontally scrollable grid for mobile */}
        <div className="card" style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }} data-testid="schedule-grid">
          <div className="grid" style={{ gridTemplateColumns: "72px repeat(7, minmax(110px, 1fr))", minWidth: 760 }}>
            <div className="label-tiny p-3 border-r border-b" style={{ borderColor: "var(--border)" }}>Time</div>
            {DAYS.map((d, di) => {
              const isActive = di === activeDow;
              return (
                <div key={d} className="label-tiny p-3 border-r border-b"
                  style={{
                    borderColor: "var(--border)",
                    color: isActive ? "var(--green)" : undefined,
                    background: isActive ? "rgba(61,220,126,0.08)" : undefined,
                  }}
                  data-testid={`day-header-${di}`}>{d}{isActive ? " ·" : ""}</div>
              );
            })}
            {times.map((t, ti) => (
              <div key={`row-${ti}`} style={{ display: "contents" }}>
                <div className="mono text-[12px] p-3 border-r border-b flex items-center"
                  style={{ borderColor: "var(--border)", color: "var(--text-dim)" }}>{t}</div>
                {DAYS.map((d, di) => {
                  const k = grid[`${di}-${t}`];
                  const isActive = di === activeDow;
                  return (
                    <div key={`${di}-${ti}`} className="p-1.5 border-r border-b"
                      style={{
                        borderColor: "var(--border)",
                        minHeight: 80,
                        background: isActive ? "rgba(61,220,126,0.04)" : undefined,
                      }}>
                      {k ? <ClassCell klass={k} onClick={() => setSelected(k)} /> : (
                        <button onClick={() => setShowForm({ defaults: { day: di, time: t } })}
                          className="w-full h-full opacity-30 hover:opacity-100 flex items-center justify-center"
                          style={{ minHeight: 70 }}
                          data-testid={`empty-${di}-${ti}`}><Plus size={14} /></button>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
        <div className="label-tiny md:hidden text-center" style={{ color: "var(--text-dim)" }}>← swipe to see other days →</div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {freshSelected && (
            <div className="card p-5 md:p-6" data-testid="class-detail">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="label" style={{ color: colorFor(freshSelected.type) }}>{freshSelected.type}</div>
                  <div className="text-[18px] md:text-[22px] font-bold uppercase mt-1 tracking-tight">
                    {freshSelected.name}
                  </div>
                  <div className="label-tiny mt-1">{DAYS[freshSelected.day_of_week]} · {freshSelected.start_time}</div>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <button onClick={() => setShowForm({ editing: freshSelected })}
                    className="btn btn-outline" style={{ padding: "8px 12px" }} data-testid="btn-edit-class">
                    <Pencil size={14} />
                  </button>
                  <button onClick={() => deleteClass(freshSelected)} disabled={busy}
                    className="btn btn-outline" style={{ padding: "8px 12px", borderColor: "rgba(255,80,80,0.4)", color: "rgb(255,120,120)" }}
                    data-testid="btn-delete-class">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
              <div className="divider my-4" />
              {[
                ["Instructor", freshSelected.instructor],
                ["Capacity", `${freshSelected.capacity} athletes`],
                ["Duration", `${freshSelected.duration_min} minutes`],
                ["Mat", freshSelected.mat],
              ].map(([l, v]) => (
                <div key={l} className="py-2 flex justify-between text-[14px] gap-3">
                  <span className="label">{l}</span><span className="text-right">{v}</span>
                </div>
              ))}

              {/* Reservations for the next occurrence */}
              <div className="divider my-4" />
              <div className="flex items-center justify-between">
                <div>
                  <div className="label" style={{ color: "var(--green)" }}>
                    <Users size={11} style={{ display: "inline", marginRight: 6, marginBottom: 2 }} />
                    Reservations
                  </div>
                  <div className="label-tiny mt-1">
                    Next class: <span className="mono">{freshSelected.next_date || "—"}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="mono text-[16px] font-bold" data-testid="class-reserved-count">
                    <span style={{ color: (freshSelected.reserved_count || 0) >= freshSelected.capacity ? "var(--warn, #f4b942)" : "var(--green)" }}>{freshSelected.reserved_count || 0}</span>
                    <span style={{ color: "var(--text-dim)" }}> / {freshSelected.capacity}</span>
                  </div>
                  <button onClick={load} className="btn btn-outline" style={{ padding: "6px 8px" }} title="Refresh" data-testid="btn-refresh-reservations">
                    <RefreshCw size={12} />
                  </button>
                </div>
              </div>
              {(freshSelected.reserved || []).length === 0 ? (
                <div className="label-tiny mt-3" style={{ color: "var(--text-dim)" }} data-testid="reservations-empty">
                  No reservations yet. Students who reserve this class in their app will appear here.
                </div>
              ) : (
                <div className="mt-3 space-y-2" data-testid="reservations-list">
                  {freshSelected.reserved.map(r => (
                    <div key={r.student_id} className="card p-2.5 flex items-center gap-3" data-testid={`reservation-row-${r.student_id}`}>
                      {r.avatar
                        ? <img src={r.avatar} alt="" className="w-8 h-8 rounded-full object-cover shrink-0"/>
                        : <div className="w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-semibold shrink-0" style={{ background: "var(--bg-elev)", border: "1px solid var(--border)" }}>{r.initials}</div>}
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-[13px] truncate">{r.name}</div>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className={`belt-bar belt-${r.belt}`} style={{ width: 20, height: 6 }}/>
                          <span className="label-tiny capitalize">{r.belt} · {r.stripes}</span>
                          {r.phone && <span className="label-tiny mono" style={{ color: "var(--text-dim)" }}>· {r.phone}</span>}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          <div className="card p-5 md:p-6">
            <div className="label mb-4">Instructors</div>
            {instructors.length === 0 ? (
              <div className="label-tiny" style={{ color: "var(--text-dim)" }}>Instructors will appear here once you add classes.</div>
            ) : (
              <div className="space-y-3">
                {instructors.map(i => (
                  <div key={i.name} className="flex items-center justify-between" data-testid={`instructor-${i.name}`}>
                    <div className="font-semibold text-[14px] uppercase tracking-tight">{i.name}</div>
                    <div className="label-tiny">{i.classes_per_week} class{i.classes_per_week === 1 ? "" : "es"}/wk</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {showForm && (
        <ClassFormModal
          defaults={showForm.defaults}
          editing={showForm.editing}
          onClose={() => setShowForm(null)}
          onSave={onSaved}
        />
      )}
      {showImport && (
        <ImportClassesModal onClose={() => setShowImport(false)} onDone={load} />
      )}
    </div>
  );
}
